import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { DataTable, TabelaPainel, type Coluna } from '@/components/patterns/data-table';
import { FilterBar, FilterBusca, FilterLimpar, FilterPeriodo, FilterSelect } from '@/components/patterns/filter-bar';
import { StatusAgendamentoBadge } from '@/components/patterns/status-badge';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { dataSource } from '@/data';
import type { Agendamento } from '@/data/types';
import { useResource } from '@/hooks/use-resource';
import { formatarData, texto } from '@/lib/format';
import { instanteAgendado, noPeriodo, PERIODO_PRESETS } from '@/lib/periodo';
import { cn } from '@/lib/utils';

/**
 * "Meus Agendamentos" — modal aberto pelo dropdown do usuário (`UserChip`),
 * mesmo estilo de chrome do `AdministracaoDialog`.
 *
 * Ao contrário de `/agendamentos` (hub de TODAS as instalações do escopo do
 * usuário — Diretório, cascata de controladora etc.), esta lista é escopada
 * por QUEM pediu o agendamento: a mesma pessoa vê o que ela mesma agendou,
 * em qualquer instalação, mesmo uma fora do seu escopo de acesso atual.
 *
 * Sem WebSocket de propósito: não é uma tela de disputa por recurso
 * compartilhado (isso já é coberto em `/agendamentos`) — aqui um polling
 * discreto (silencioso, sem piscar "carregando") já dá fidelidade suficiente
 * pra pegar Programado → Executando/Concluído sem recarregar a página.
 */
const INTERVALO_ATUALIZACAO_MS = 45_000;

export function MeusAgendamentosDialog({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const carregar = useCallback((signal: AbortSignal) => dataSource.listMeusAgendamentos(signal), []);
  const recurso = useResource<Agendamento[]>(carregar, [], { intervaloAtualizacaoMs: INTERVALO_ATUALIZACAO_MS });

  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [periodo, setPeriodo] = useState<string | null>(null);
  const [periodoDe, setPeriodoDe] = useState('');
  const [periodoAte, setPeriodoAte] = useState('');

  const agendamentos = recurso.dados ?? [];
  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return agendamentos.filter((item) => {
      if (status && item.status !== status) return false;
      if (!noPeriodo(instanteAgendado(item), periodo, periodoDe, periodoAte)) return false;
      if (!termo) return true;
      return `${item.instalacao} ${item.versao} ${item.batchId ?? ''}`.toLowerCase().includes(termo);
    });
  }, [agendamentos, busca, status, periodo, periodoDe, periodoAte]);

  const filtrosAtivos = [busca.trim() !== '', status !== null, periodo !== null].filter(Boolean).length;

  const colunas: Array<Coluna<Agendamento>> = [
    {
      id: 'instalacao',
      cabecalho: 'Instalação',
      ordenarPor: (item) => item.instalacao,
      celula: (item) => (
        <div className="flex flex-col gap-0.5">
          <span className="font-bold">{texto(item.instalacao)}</span>
          <span className="font-mono text-[11px] text-on-surface-muted">{texto(item.id)}</span>
        </div>
      ),
    },
    {
      id: 'quando',
      cabecalho: 'Data e horário',
      largura: '180px',
      ordenarPor: (item) => `${item.data ?? ''}${item.hora}`,
      celula: (item) => (
        <div className="flex flex-col gap-0.5">
          <span className="tabular-nums">{formatarData(item.data)}</span>
          <span className="text-micro font-normal uppercase tracking-[0.04em] text-on-surface-muted">
            às {texto(item.hora)}
          </span>
        </div>
      ),
    },
    {
      id: 'versao',
      cabecalho: 'Versão',
      largura: '130px',
      ordenarPor: (item) => item.versao,
      celula: (item) => <span className="font-narrow text-[15px] font-bold tabular-nums">{texto(item.versao)}</span>,
    },
    {
      id: 'status',
      cabecalho: 'Status',
      largura: '170px',
      celula: (item) => <StatusAgendamentoBadge valor={item.status} />,
    },
    {
      id: 'origem',
      cabecalho: 'Origem',
      largura: '200px',
      secundaria: true,
      celula: (item) => (
        <div className="flex flex-col items-start gap-1">
          <span>{texto(item.origem)}</span>
          {item.batchId ? (
            <Badge tone="primary">
              Lote <span className="font-mono text-[10px]">{item.batchId}</span>
            </Badge>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <Dialog open={aberto} onOpenChange={(valor) => !valor && aoFechar()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-bg-overlay duration-slow ease-standard data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 flex h-[min(38rem,90vh)] w-[min(64rem,95vw)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-card border border-border bg-bg-surface shadow-lg',
            'duration-slow ease-standard data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          )}
        >
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <div className="flex min-w-0 flex-col gap-0.5">
              <DialogPrimitive.Title className="text-card-title">Meus agendamentos</DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-cell text-on-surface-muted">
                O que você agendou, em qualquer instalação.
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close className="rounded-button p-2 text-icon transition-colors duration-fast ease-standard hover:bg-bg-subtle hover:text-on-surface">
              <X className="size-4" aria-hidden="true" />
              <span className="sr-only">Fechar</span>
            </DialogPrimitive.Close>
          </div>

          <div className="pan-scroll-area min-h-0 flex-1 overflow-y-auto p-5">
            <TabelaPainel
              filtros={
                <FilterBar>
                  <FilterBusca
                    id="filtro-busca-meus-agendamentos"
                    label="Buscar agendamento"
                    valor={busca}
                    aoMudar={setBusca}
                    placeholder="Instalação, versão ou lote"
                  />
                  <FilterSelect
                    id="filtro-status-meus-agendamentos"
                    label="Status"
                    valor={status}
                    aoMudar={setStatus}
                    rotuloTodos="Todos os status"
                    opcoes={[
                      { valor: 'Programado', rotulo: 'Programado' },
                      { valor: 'Executando', rotulo: 'Executando' },
                      { valor: 'Concluído', rotulo: 'Concluído' },
                      { valor: 'Falha', rotulo: 'Falhou' },
                      { valor: 'Falha Parcial', rotulo: 'Falha parcial' },
                      { valor: 'Cancelado', rotulo: 'Cancelado' },
                    ]}
                  />
                  <FilterPeriodo
                    id="filtro-periodo-meus-agendamentos"
                    periodo={periodo}
                    de={periodoDe}
                    ate={periodoAte}
                    aoMudar={({ periodo: proximo, de, ate }) => {
                      setPeriodo(proximo);
                      setPeriodoDe(de);
                      setPeriodoAte(ate);
                    }}
                    presets={PERIODO_PRESETS}
                    limiteDias={31}
                  />
                  <FilterLimpar
                    ativo={filtrosAtivos > 0}
                    quantidade={filtrosAtivos}
                    aoLimpar={() => {
                      setBusca('');
                      setStatus(null);
                      setPeriodo(null);
                      setPeriodoDe('');
                      setPeriodoAte('');
                    }}
                  />
                </FilterBar>
              }
            >
              <DataTable
                rotulo="Meus agendamentos"
                itens={filtrados}
                colunas={colunas}
                chave={(item) => item.id}
                estado={recurso.estado}
                erro={recurso.erro}
                aoTentarDeNovo={recurso.recarregar}
                vazio={
                  filtrosAtivos > 0
                    ? {
                        titulo: 'Nenhum agendamento com esses filtros',
                        descricao: 'Ajuste os filtros para ver os demais agendamentos que você fez.',
                      }
                    : {
                        titulo: 'Você ainda não agendou nada',
                        descricao: 'Agendamentos que você criar, em qualquer instalação, aparecem aqui.',
                      }
                }
              />
            </TabelaPainel>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </Dialog>
  );
}
